/*
 * Copyright (c) 2019.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Vladimir Ganturin <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {HttpClient, HttpEventType, HttpHeaders, HttpParams, HttpRequest} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {ApiResponse} from '../interfaces/ApiResponse';
import {Environment} from '../interfaces/Environment';
import {SortPipe} from '../pipes/sort.pipe';
import {TuiDay, TuiDayRange} from '@taiga-ui/cdk'

@Injectable({
  providedIn: 'root',
})

/**
 *  Main API interconnection
 *  service with a number of necessary methods
 *
 */
export class CompoApiService {
  private readonly options = {};
  private readonly headers: HttpHeaders;

  private keyMap: object;

  constructor(private readonly domSanitizer: DomSanitizer, private readonly http: HttpClient,
              @Inject('environment') private readonly environment: Environment) {
    this.headers = new HttpHeaders({'Content-Type': 'application/json', 'ngsw-bypass': 1});
    this.options = {
      headers: this.headers,
      withCredentials: true
    };

    this.keyMap = this.createKeyMap();
  }

  post<T, E>(url, data: T): Observable<ApiResponse<E>> {
    return this.http.post<ApiResponse<E>>(
      this.environment.serverApi + String(url),
      data,
      this.options
    );
  }

  get<T>(url): Observable<ApiResponse<T>> {
    return this.http.get<ApiResponse<T>>(
      this.environment.serverApi + String(url),
      this.options
    );
  }

  getWithParams<T>(url: string, params: HttpParams): Observable<ApiResponse<T>> {
    const options = {
      headers: this.headers,
      withCredentials: true,
      params
    };

    return this.http.get<ApiResponse<T>>(
      this.environment.serverApi + url,
      options
    );
  }

  delete<T>(url): Observable<ApiResponse<T>> {
    return this.http.delete<ApiResponse<T>>(
      this.environment.serverApi + String(url),
      this.options
    );
  }

  remove(url) {
    return this.http.delete(
      this.environment.serverApi + String(url),
      this.options
    );
  }

  patch<T, E>(url, data: T): Observable<ApiResponse<E>> {
    return this.http.patch<ApiResponse<E>>(
      this.environment.serverApi + String(url),
      data,
      this.options
    );
  }

  put<T, E>(url, data: T): Observable<ApiResponse<E>> {
    return this.http.put<ApiResponse<E>>(
      this.environment.serverApi + String(url),
      data,
      this.options
    );
  }

  head(url) {
    return this.http.head(
      this.environment.serverApi + String(url),
      this.options
    );
  }

  /**
   * Downloads files via api with token credentials
   * @param url download controller uri
   */
  downloadUrl(url: string): Observable<Blob> {
    const downloadHeaders = new HttpHeaders({'Accept': 'application/octet-stream', 'ngsw-bypass': 1});

    return this.http.get(this.getBaseUrl(url), {headers: downloadHeaders, responseType: 'blob', withCredentials: true});
  }

  private getBaseUrl(url: string) {
    return url.indexOf('http') !== 0 ? this.environment.serverApi + String(url) : url;
  }

  downloadPostUrl<T>(url: string, data: T): Observable<Blob> {
    const downloadHeaders = new HttpHeaders({'Accept': 'application/octet-stream', 'ngsw-bypass': 1});

    return this.http.post(this.getBaseUrl(url), data, {headers: downloadHeaders, responseType: 'blob', withCredentials: true});
  }

  /**
   * Post request as multipart form data
   * @param url
   * @param data
   */
  postForm<T, E>(url, data: T) {
    return this.http.post<ApiResponse<E>>(
      this.environment.serverApi + String(url),
      this.buildFormData(data),
      {
        withCredentials: true,
        reportProgress: true
      }
    );
  }

  postFormWithProgress<T, E>(url, data: T): Observable<{ progress?: number, response?: ApiResponse<E> }> {

    const req = new HttpRequest(
      'POST',
      this.environment.serverApi + String(url),
      this.buildFormData(data),
      {
        reportProgress: true, // Включаем отслеживание прогресса
        withCredentials: true
      }
    );

    return this.http.request(req).pipe(
      map(event => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            // Вычисляем прогресс загрузки
            const progress = Math.round(100 * event.loaded / (event.total || 1));
            console.log(progress + ' progress');
            return {progress};

          case HttpEventType.Response:
            // Когда получаем ответ
            return {response: event.body as ApiResponse<E>};

          default:
            return {};
        }
      })
    );
  }

  putForm<T, E>(url, data: T) {
    return this.http.put<ApiResponse<E>>(
      this.environment.serverApi + String(url),
      this.jsonToFormData(data),
      {
        withCredentials: true
      }
    );
  }

  /**
   * Method is useful when it's necessary to submit a form (not a JSON object) to server
   * @param formValue
   */
  private buildFormData(formValue): FormData {

    const formData = new FormData();

    for (const key of Object.keys(formValue)) {

      const value = formValue[key];
      if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    }

    return formData;
  }

  private getFormData(formData, data, parentKey) {
    if (data && typeof data === 'object' && !(data instanceof Date) && !(data instanceof File)) {
      Object.keys(data).forEach(key => {
        this.getFormData(formData, data[key], parentKey ? `${parentKey}.${key}` : key);
      });
    } else {
      const value = data == null ? '' : data;

      formData.append(parentKey, value);
    }
  }

  private jsonToFormData(data) {
    const formData = new FormData();

    this.getFormData(formData, data, false);

    return formData;
  }

  getDocumentImageBlob(url: string): Observable<SafeUrl> {
    return this.http
      .get(this.getBaseUrl(url), {
        responseType: 'blob',
        withCredentials: true
      }).pipe(map(e => this.domSanitizer.bypassSecurityTrustUrl(URL.createObjectURL(e))));
  }

  naturalSort(arr: any[], field: string): any[] {
    return SortPipe.naturalSort(arr, field);
  }

  createKeyMap() {
    const map = {};
    'qwertyuiop[]asdfghjkl;\'zxcvbnm,./{:><},'.split('').forEach((enChar, index) => {
      const ruChar = 'йцукенгшщзхъфывапролджэячсмитьбю.хжюбъб'.split('')[index];
      const code = `Key${enChar.toUpperCase()}`;
      map[enChar] = code;
      map[ruChar] = code;
    });
    return map;
  }

  stringToKeyCodes(str: string) {
    str = str
      .replace('[', '{')
      .replace(']', '}')

    return str
      .toLowerCase()
      .split('')
      .map(char => this.keyMap[char] || 'Unknown')
      .join('');
  }

  convertDate(date: Date): string {
    const formatter = new Intl.DateTimeFormat(this.environment.locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });

    const parts = formatter.formatToParts(date);
    const getPart = (type) => parts.find(part => part.type === type).value;

    return `${getPart('year')}-${getPart('month')}-${getPart('day')}`;
  }

  convertDateTime(date: Date): string {
    const formatter = new Intl.DateTimeFormat(this.environment.locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });

    const parts = formatter.formatToParts(date);
    const getPart = (type) => parts.find(part => part.type === type).value;

    return `${getPart('year')}-${getPart('month')}-${getPart('day')}T${getPart('hour')}:${getPart('minute')}:${getPart('second')}`;
  }

  endOfToday(): Date {
    const date = new Date();
    date.setHours(23, 59, 59, 999);
    return date;
  }

  startOfMonth(date?: Date): Date {
    const targetDate = date || new Date();
    return new Date(targetDate.getFullYear(), targetDate.getMonth(), 1);
  }

  getDateRange(params: HttpParams): TuiDayRange {
    const dateStart = params.get('dateStart');
    const dateEnd = params.get('dateEnd');

    if (dateStart && dateEnd) {
      const from = new Date(dateStart);
      const to = new Date(dateEnd);

      return new TuiDayRange(
        new TuiDay(from.getFullYear(), from.getMonth(), from.getDate()),
        new TuiDay(to.getFullYear(), to.getMonth(), to.getDate())
      );
    }
  }

}
